import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { getInitials } from "@/lib/utils";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MessageSquare, Send } from "lucide-react";
import { useMessagesEnabled } from "@/hooks/useMessagesEnabled";

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256";

type InlineMemberCardProps = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  active?: boolean;
  permissableLocations?: string[];
  introduction?: string;

  withLabel?: boolean;
  /** When false, render a plain (non-clickable) avatar + name with no detail drawer. Default true. */
  interactive?: boolean;
  /** When true (interactive only), show a "Message {firstName}" button that opens the messaging system. */
  showMessageButton?: boolean;
};

const InlineMemberCard = ({
  id,
  firstName,
  lastName,
  email,
  avatar,
  active = true,
  withLabel = true,
  interactive = true,
  showMessageButton = false,
}: InlineMemberCardProps) => {
  const navigate = useNavigate();
  const messagesEnabled = useMessagesEnabled();
  const [isMemberCardOpen, setIsMemberCardOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fake fetch: when sheet opens, set loading true, then after timeout set false
  const handleIsOpenChange = (open: boolean) => {
    setIsMemberCardOpen(open);

    if (open) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000); // Simulate 1s fetch
    }
  };

  // Open the websocket messaging system with this member pre-selected. MessagesView resolves the
  // recipient to an existing conversation (joins it) or opens a new one if none exists.
  const handleMessage = () => {
    navigate("/messages", {
      state: {
        recipient: {
          type: "member",
          id,
          firstName,
          lastName,
          email,
          avatar,
          active,
        },
      },
    });
  };

  const avatarAndName = (
    <>
      <Avatar className="w-6 h-6">
        <AvatarImage src={avatar || DEFAULT_AVATAR} alt={firstName || "User Avatar"} />
        <AvatarFallback>{getInitials(`${firstName} ${lastName}`)}</AvatarFallback>
      </Avatar>
      {withLabel && (
        <span className="pl-2">
          {firstName} {lastName}
        </span>
      )}
    </>
  );

  // Non-interactive: a plain avatar + name with no click affordance and no detail drawer.
  if (!interactive) {
    return <div className="flex items-center pr-2">{avatarAndName}</div>;
  }

  return (
    <>
      <div>
        <div
          className="flex items-center hover:cursor-pointer hover:bg-gray-200 rounded-lg pr-2"
          data-testid="inline-member-card-trigger"
          onClick={() => handleIsOpenChange(true)}
        >
          {avatarAndName}
        </div>
        <div className={isMemberCardOpen ? `h-auto visible flex` : `invisible h-0 hidden`}>
          <ScrollArea>
            <Sheet open={isMemberCardOpen} onOpenChange={handleIsOpenChange}>
              <SheetContent className="flex p-6 w-full">
                {isLoading ? (
                  <>
                    <SheetTitle className="sr-only">Loading</SheetTitle>
                    <SheetDescription className="sr-only">Loading member details</SheetDescription>
                    <div className="flex items-center justify-center h-32 w-full">
                      <Spinner className="size-6 mr-2" />
                      <span className="text-lg">Loading member...</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col w-full">
                      <SheetTitle className="text-3xl flex items-center gap-4 mb-2">
                        <Avatar className="w-24 h-24">
                          <AvatarImage src={avatar || DEFAULT_AVATAR} alt={firstName || "User Avatar"} />
                          <AvatarFallback>{getInitials(`${firstName} ${lastName}`)}</AvatarFallback>
                        </Avatar>
                        <span>
                          {firstName} {lastName}
                        </span>
                      </SheetTitle>
                      <SheetDescription className="sr-only">
                        Contact details for {firstName} {lastName}
                      </SheetDescription>

                      <div className="flex flex-col py-3">
                        <dt className="mb-1 text-gray-500 md:text-lg dark:text-gray-400">Email</dt>
                        <dd className="text-lg font-semibold flex gap-2">
                          <Send className="w-5 h-5 mt-1" />{" "}
                          <a
                            href={`mailto:${email}`}
                            className="text-blue-800 hover:text-blue-900 truncate"
                            title="Send Email"
                          >
                            {email}
                          </a>
                        </dd>
                      </div>

                      <div className="flex flex-col py-3 mt-16 gap-4 justify-center items-center w-full">
                        {showMessageButton && messagesEnabled && (
                          <Button
                            variant="default"
                            data-testid="inline-member-card-message"
                            onClick={handleMessage}
                            className="w-full"
                          >
                            <MessageSquare /> Message {firstName}
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => setIsMemberCardOpen(false)} className="w-full">
                          Close
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </SheetContent>
            </Sheet>
          </ScrollArea>
        </div>
      </div>
    </>
  );
};

export default InlineMemberCard;
